/**
 * Admin Page - Global Settings Management
 *
 * DEC-049 Phase 5: Admin UI for system settings
 * Requires is_admin=true on user account
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import api from '../api/client'
import Toggle from '../components/ui/Toggle'

const apiUrl = import.meta.env.VITE_API_URL || ''

interface GlobalSettings {
  // Agent settings
  research_enabled: boolean
  research_interval_days: number
  research_auto_apply: boolean
  pharma_web_enabled: boolean
  pharma_sources: Record<string, boolean> | null
  dossier_auto_update: boolean

  // Debug settings
  debug_mode_default: boolean
  debug_agents_visible: Record<string, boolean> | null

  // Model settings
  default_therapist_model: string
  opus_budget_daily: number
  haiku_budget_daily: number

  // Safety settings
  crisis_escalation_enabled: boolean
  crisis_notification_email: string | null

  // Maintenance
  maintenance_mode: boolean
  maintenance_message: string | null

  // Metadata
  updated_at: string
  updated_by: string | null
}

// =============================================================================
// USER MANAGEMENT SECTION
// =============================================================================

interface AdminUser {
  id: number
  email: string | null
  display_name: string | null
  role: string
  is_active: boolean
  is_admin: boolean
  created_at: string
  last_activity_at: string | null
  total_sessions: number
  current_therapist: string | null
  telegram_linked: boolean
}

interface UserListResponse {
  users: AdminUser[]
  total: number
  page: number
  page_size: number
}

const roleBadgeStyles: Record<string, string> = {
  admin: 'bg-blue-100 text-blue-700',
  clinician: 'bg-green-100 text-green-700',
  user: 'bg-neutral-100 text-neutral-500',
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${roleBadgeStyles[role] || roleBadgeStyles.user}`}>
      {role}
    </span>
  )
}

function UserManagement() {
  const [suggestions, setSuggestions] = useState<AdminUser[]>([])
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<number | null>(null)
  const currentUser = useAuthStore((state) => state.user)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const comboboxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const fetchSuggestions = useCallback(async (s: string, r: string) => {
    if (s.length < 2 && !r) {
      setSuggestions([])
      setShowDropdown(false)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams()
      params.set('page', '1')
      params.set('page_size', '10')
      if (s) params.set('search', s)
      if (r) params.set('role', r)

      const response = await api.get<UserListResponse>(`/admin/users?${params.toString()}`)
      setSuggestions(response.data.users)
      setShowDropdown(true)
      setHighlightIndex(-1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search users')
      setSuggestions([])
      setShowDropdown(false)
    } finally {
      setLoading(false)
    }
  }, [])

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // Click-outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSelectedUser(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value, roleFilter)
    }, 300)
  }

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value)
    setSelectedUser(null)
    fetchSuggestions(search, value)
  }

  const handleSelectUser = (user: AdminUser) => {
    setSelectedUser(user)
    setShowDropdown(false)
    setSearch(user.display_name || user.email || `User #${user.id}`)
  }

  const clearSelection = () => {
    setSelectedUser(null)
    setSearch('')
    setSuggestions([])
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex(prev => (prev + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1))
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault()
      handleSelectUser(suggestions[highlightIndex])
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const updateUser = async (userId: number, data: { role?: string; is_active?: boolean }) => {
    setUpdatingId(userId)
    setError(null)

    try {
      const response = await api.patch<AdminUser>(`/admin/users/${userId}`, data)
      if (selectedUser?.id === userId) {
        setSelectedUser(response.data)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update user'
      setError(msg)
      setTimeout(() => setError(null), 3000)
    } finally {
      setUpdatingId(null)
    }
  }

  const isSelf = (userId: number) => currentUser?.id === userId

  return (
    <section className="card" data-testid="admin-section-users">
      <h2 className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-4">
        User Management
      </h2>

      {/* Combobox container */}
      <div ref={comboboxRef} className="relative">
        {/* Search + Filter */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (suggestions.length > 0 && !selectedUser) setShowDropdown(true) }}
              placeholder="Search by name or email..."
              role="combobox"
              aria-expanded={showDropdown}
              aria-autocomplete="list"
              className="w-full px-4 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full" />
              </div>
            )}
          </div>
          <select
            value={roleFilter}
            onChange={(e) => handleRoleFilterChange(e.target.value)}
            className="px-3 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          >
            <option value="">All roles</option>
            <option value="user">User</option>
            <option value="clinician">Clinician</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        {/* Autocomplete dropdown */}
        {showDropdown && !selectedUser && (
          <div
            className="absolute z-20 left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-xl shadow-lg max-h-80 overflow-y-auto"
            role="listbox"
          >
            {suggestions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-neutral-500">Пользователи не найдены</div>
            ) : suggestions.map((u, idx) => (
              <button
                key={u.id}
                onClick={() => handleSelectUser(u)}
                role="option"
                aria-selected={idx === highlightIndex}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  idx === highlightIndex ? 'bg-primary-50' : 'hover:bg-neutral-50'
                } ${idx > 0 ? 'border-t border-neutral-100' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-neutral-800 truncate">
                      {u.display_name || u.email || `User #${u.id}`}
                    </span>
                    <RoleBadge role={u.role} />
                    {u.telegram_linked && (
                      <span className="text-xs text-blue-500">TG</span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 truncate">{u.email || 'No email'}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-error-light text-error-dark rounded-xl p-3 text-sm mb-4">
          {error}
        </div>
      )}

      {/* Selected user card */}
      {selectedUser && (
        <div className={`p-4 rounded-xl border border-neutral-200 bg-white ${updatingId === selectedUser.id ? 'opacity-50' : ''}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-neutral-800">
                {selectedUser.display_name || selectedUser.email || `User #${selectedUser.id}`}
              </h3>
              <RoleBadge role={selectedUser.role} />
              {selectedUser.telegram_linked && (
                <span className="text-xs text-blue-500" title="Telegram linked">TG</span>
              )}
              {isSelf(selectedUser.id) && (
                <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded">you</span>
              )}
            </div>
            <button
              onClick={clearSelection}
              className="p-1 text-neutral-400 hover:text-neutral-600 rounded-full"
              title="Clear selection"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Details */}
          <div className="text-xs text-neutral-500 space-y-1 mb-4">
            <p>{selectedUser.email || 'No email'}</p>
            <p>
              {selectedUser.total_sessions} sessions
              {selectedUser.last_activity_at && ` \u00B7 last active: ${new Date(selectedUser.last_activity_at).toLocaleDateString('ru-RU')}`}
            </p>
            <p>
              {selectedUser.current_therapist && `Therapist: ${selectedUser.current_therapist} \u00B7 `}
              Created: {new Date(selectedUser.created_at).toLocaleDateString('ru-RU')}
            </p>
          </div>

          {/* Role management */}
          <div className="flex items-center gap-3 pt-3 border-t border-neutral-100">
            <label className="text-xs text-neutral-500">Role:</label>
            <select
              value={selectedUser.role}
              onChange={(e) => updateUser(selectedUser.id, { role: e.target.value })}
              disabled={updatingId === selectedUser.id || isSelf(selectedUser.id)}
              className="px-2 py-1 border border-neutral-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="user">user</option>
              <option value="clinician">clinician</option>
              <option value="admin">admin</option>
            </select>

            <div className="flex-1" />

            <label className="text-xs text-neutral-500">Active:</label>
            <Toggle
              enabled={selectedUser.is_active}
              onChange={(v) => updateUser(selectedUser.id, { is_active: v })}
              disabled={updatingId === selectedUser.id || isSelf(selectedUser.id)}
            />
          </div>
        </div>
      )}
    </section>
  )
}


export default function AdminPage() {
  const navigate = useNavigate()
  const token = useAuthStore((state) => state.accessToken)
  const user = useAuthStore((state) => state.user)

  const [settings, setSettings] = useState<GlobalSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Check admin access
  const isAdmin = user?.isAdmin === true

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!isAdmin) {
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`${apiUrl}/api/v1/admin/settings`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.status === 403) {
          setError('Admin access required')
          setLoading(false)
          return
        }

        if (!response.ok) {
          throw new Error('Failed to fetch settings')
        }

        const data = await response.json()
        setSettings(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchSettings()
  }, [token, isAdmin])

  // Update single setting
  const updateSetting = async (key: keyof GlobalSettings, value: unknown) => {
    if (!settings) return

    setSettings({ ...settings, [key]: value })

    try {
      setSaving(true)
      setError(null)

      const response = await fetch(`${apiUrl}/api/v1/admin/settings`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [key]: value }),
      })

      if (!response.ok) {
        throw new Error('Failed to update setting')
      }

      const data = await response.json()
      setSettings(data)
      setSuccess('Settings updated')
      setTimeout(() => setSuccess(null), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
      // Revert on error
      setSettings(settings)
    } finally {
      setSaving(false)
    }
  }

  // No admin access
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-calm flex items-center justify-center" data-testid="admin-access-denied">
        <div className="card text-center p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-error-light flex items-center justify-center">
            <svg className="w-8 h-8 text-error-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-800 mb-2">Access Denied</h2>
          <p className="text-neutral-600 mb-6">Admin privileges required to access this page.</p>
          <button onClick={() => navigate('/chat')} className="btn btn-primary">
            Back to Chat
          </button>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-calm flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-calm" data-testid="admin-page">
      {/* Header */}
      <header className="glass border-b border-neutral-200/50 sticky top-0 z-10 safe-area-pt">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/settings')}
              className="p-2 -ml-2 hover:bg-neutral-100 rounded-full transition-colors"
            >
              <svg className="w-6 h-6 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="ml-2 text-lg font-semibold text-neutral-800">Admin Settings</h1>
          </div>
          {saving && (
            <span className="text-sm text-neutral-500">Saving...</span>
          )}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 animate-fade-in safe-area-pb">
        {/* Error/Success messages */}
        {error && (
          <div className="bg-error-light text-error-dark rounded-xl p-4 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-success-light text-success-dark rounded-xl p-4 text-sm">
            {success}
          </div>
        )}

        {/* User Management — always visible for admin */}
        <UserManagement />

        {settings && (
          <>
            {/* Agent Settings */}
            <section className="card" data-testid="admin-section-agent">
              <h2 className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-4">
                Agent Settings
              </h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-800">Research Enabled</p>
                    <p className="text-sm text-neutral-500">Enable MethodologyResearcher weekly runs</p>
                  </div>
                  <Toggle
                    enabled={settings.research_enabled}
                    onChange={(v) => updateSetting('research_enabled', v)}
                    disabled={saving}
                  />
                </div>

                <div className="border-t border-neutral-100" />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-800">Research Auto-Apply</p>
                    <p className="text-sm text-neutral-500">Automatically apply research suggestions</p>
                  </div>
                  <Toggle
                    enabled={settings.research_auto_apply}
                    onChange={(v) => updateSetting('research_auto_apply', v)}
                    disabled={saving}
                  />
                </div>

                <div className="border-t border-neutral-100" />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-800">Pharma Web Research</p>
                    <p className="text-sm text-neutral-500">Enable openFDA/DailyMed lookups</p>
                  </div>
                  <Toggle
                    enabled={settings.pharma_web_enabled}
                    onChange={(v) => updateSetting('pharma_web_enabled', v)}
                    disabled={saving}
                  />
                </div>

                <div className="border-t border-neutral-100" />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-800">Dossier Auto-Update</p>
                    <p className="text-sm text-neutral-500">Update dossier after each session</p>
                  </div>
                  <Toggle
                    enabled={settings.dossier_auto_update}
                    onChange={(v) => updateSetting('dossier_auto_update', v)}
                    disabled={saving}
                  />
                </div>
              </div>
            </section>

            {/* Debug Settings */}
            <section className="card" data-testid="admin-section-debug">
              <h2 className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-4">
                Debug Settings
              </h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-800">Debug Mode Default</p>
                    <p className="text-sm text-neutral-500">Show hidden agents by default</p>
                  </div>
                  <Toggle
                    enabled={settings.debug_mode_default}
                    onChange={(v) => updateSetting('debug_mode_default', v)}
                    disabled={saving}
                  />
                </div>
              </div>
            </section>

            {/* Model Settings */}
            <section className="card" data-testid="admin-section-model">
              <h2 className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-4">
                Model Settings
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block font-medium text-neutral-800 mb-2">
                    Default Therapist Model
                  </label>
                  <select
                    value={settings.default_therapist_model}
                    onChange={(e) => updateSetting('default_therapist_model', e.target.value)}
                    disabled={saving}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="sonnet">Sonnet (default)</option>
                    <option value="opus">Opus (expensive)</option>
                    <option value="haiku">Haiku (fast)</option>
                  </select>
                </div>

                <div className="border-t border-neutral-100" />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium text-neutral-800 mb-2">
                      Opus Budget ($/day)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={settings.opus_budget_daily}
                      onChange={(e) => updateSetting('opus_budget_daily', parseFloat(e.target.value))}
                      disabled={saving}
                      className="w-full px-4 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-neutral-800 mb-2">
                      Haiku Budget ($/day)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="50"
                      value={settings.haiku_budget_daily}
                      onChange={(e) => updateSetting('haiku_budget_daily', parseFloat(e.target.value))}
                      disabled={saving}
                      className="w-full px-4 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Safety Settings */}
            <section className="card" data-testid="admin-section-safety">
              <h2 className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-4">
                Safety Settings
              </h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-800">Crisis Escalation</p>
                    <p className="text-sm text-neutral-500">Auto-escalate to Opus on crisis</p>
                  </div>
                  <Toggle
                    enabled={settings.crisis_escalation_enabled}
                    onChange={(v) => updateSetting('crisis_escalation_enabled', v)}
                    disabled={saving}
                  />
                </div>

                <div className="border-t border-neutral-100" />

                <div>
                  <label className="block font-medium text-neutral-800 mb-2">
                    Crisis Notification Email
                  </label>
                  <input
                    type="email"
                    value={settings.crisis_notification_email || ''}
                    onChange={(e) => updateSetting('crisis_notification_email', e.target.value || null)}
                    disabled={saving}
                    placeholder="admin@example.com"
                    className="w-full px-4 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </section>

            {/* Maintenance Mode */}
            <section className="card border-2 border-warning" data-testid="admin-section-maintenance">
              <h2 className="text-xs font-medium text-warning-dark uppercase tracking-wider mb-4">
                Maintenance Mode
              </h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-800">Enable Maintenance</p>
                    <p className="text-sm text-neutral-500">Block non-admin users</p>
                  </div>
                  <Toggle
                    enabled={settings.maintenance_mode}
                    onChange={(v) => updateSetting('maintenance_mode', v)}
                    disabled={saving}
                  />
                </div>

                {settings.maintenance_mode && (
                  <>
                    <div className="border-t border-neutral-100" />
                    <div>
                      <label className="block font-medium text-neutral-800 mb-2">
                        Maintenance Message
                      </label>
                      <textarea
                        value={settings.maintenance_message || ''}
                        onChange={(e) => updateSetting('maintenance_message', e.target.value || null)}
                        disabled={saving}
                        rows={3}
                        placeholder="System is under maintenance..."
                        className="w-full px-4 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      />
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Metadata */}
            <section className="card bg-neutral-50">
              <p className="text-sm text-neutral-500">
                Last updated: {new Date(settings.updated_at).toLocaleString()}
                {settings.updated_by && ` by ${settings.updated_by}`}
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  )
}
